import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ChevronRight, Copy } from 'lucide-react';

import type { SalesOrderShipment } from '../../services/types';
import { parseCalendarDate } from '../../utils/calendarDate';

interface SalesOrderShipmentsTableProps {
  shipments: SalesOrderShipment[];
  isLoading: boolean;
  error: string | null;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const date = parseCalendarDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-CA');
}

/**
 * Format a shipped quantity with its source in parentheses (bin or job).
 * Returns an em dash when nothing shipped from that source.
 */
function formatQtyWithSource(qty: number, source: string): string {
  if (!qty) return '—';
  const formattedQty = Number(qty.toFixed(2)).toString();
  return source ? `${formattedQty} (${source})` : formattedQty;
}

export const SalesOrderShipmentsTable: React.FC<
  SalesOrderShipmentsTableProps
> = ({ shipments, isLoading, error }) => {
  const { t } = useTranslation();
  const [expandedPacks, setExpandedPacks] = useState<Set<number>>(new Set());
  const [copiedPack, setCopiedPack] = useState<number | null>(null);

  const copyTrackingNumber = (packNum: number, trackingNumber: string) => {
    navigator.clipboard.writeText(trackingNumber);
    setCopiedPack(packNum);
    setTimeout(() => setCopiedPack(null), 2000);
  };

  const toggleShipment = (packNum: number) => {
    setExpandedPacks((prev) => {
      const next = new Set(prev);
      if (next.has(packNum)) {
        next.delete(packNum);
      } else {
        next.add(packNum);
      }
      return next;
    });
  };

  const headerCellClass =
    'px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight';
  const detailHeaderCellClass =
    'px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight';

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm">
      <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
        <h2 className="text-lg font-bold text-blue-900">
          {t('salesOrder.shipments', 'Shipments')}
          {!isLoading && !error && ` (${shipments.length})`}
        </h2>
      </div>

      {isLoading && (
        <div className="p-8 text-center text-gray-500">
          {t('salesOrder.loadingShipments', 'Loading shipments...')}
        </div>
      )}

      {error && !isLoading && (
        <div className="p-4 text-sm text-red-700">
          {t('salesOrder.shipmentsError', 'Failed to load shipments')}: {error}
        </div>
      )}

      {!isLoading && !error && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="w-10 px-2 py-3" />
                <th className={headerCellClass}>
                  {t('salesOrder.pack', 'Pack')}
                </th>
                <th className={headerCellClass}>
                  {t('salesOrder.shipDate', 'Ship Date')}
                </th>
                <th className={headerCellClass}>
                  {t('salesOrder.shipPerson', 'User')}
                </th>
                <th className={headerCellClass}>
                  {t('salesOrder.shipVia', 'Ship Via')}
                </th>
                <th className={headerCellClass}>
                  {t('salesOrder.trackingNumber', 'Tracking #')}
                </th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((shipment) => {
                const isExpanded = expandedPacks.has(shipment.packNum);

                return (
                  <React.Fragment key={shipment.packNum}>
                    <tr
                      onClick={() => toggleShipment(shipment.packNum)}
                      className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                        isExpanded ? 'bg-blue-50/40' : ''
                      }`}
                    >
                      <td className="px-2 py-3 text-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleShipment(shipment.packNum);
                          }}
                          aria-expanded={isExpanded}
                          aria-label={t(
                            'common.expandCollapse',
                            'Expand/Collapse'
                          )}
                          className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown size={16} className="text-gray-600" />
                          ) : (
                            <ChevronRight size={16} className="text-gray-600" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-medium">
                        {shipment.packNum}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {formatDate(shipment.shipDate)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {shipment.shipPerson || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {shipment.shipViaCode || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {shipment.trackingNumber ? (
                          <span className="inline-flex items-center gap-1.5">
                            {shipment.trackingNumber.toUpperCase()}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyTrackingNumber(
                                  shipment.packNum,
                                  shipment.trackingNumber.toUpperCase()
                                );
                              }}
                              title={
                                copiedPack === shipment.packNum
                                  ? t('salesOrder.copied', 'Copied!')
                                  : t(
                                      'salesOrder.copyTrackingNumber',
                                      'Copy tracking number'
                                    )
                              }
                              aria-label={t(
                                'salesOrder.copyTrackingNumber',
                                'Copy tracking number'
                              )}
                              className="p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                            >
                              {copiedPack === shipment.packNum ? (
                                <Check size={14} className="text-green-600" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-gray-200">
                        <td colSpan={6} className="bg-gray-50 px-4 py-3">
                          <div className="ml-6 overflow-x-auto rounded border border-gray-200 bg-white">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-100 border-b border-gray-200">
                                  <th className={detailHeaderCellClass}>
                                    {t('salesOrder.line', 'Line')}
                                  </th>
                                  <th className={detailHeaderCellClass}>
                                    {t('salesOrder.partNum', 'Part #')}
                                  </th>
                                  <th className={detailHeaderCellClass}>
                                    {t('salesOrder.description', 'Description')}
                                  </th>
                                  <th
                                    className={`${detailHeaderCellClass} text-right`}
                                  >
                                    {t('salesOrder.invQty', 'Inv Qty')}
                                  </th>
                                  <th
                                    className={`${detailHeaderCellClass} text-right`}
                                  >
                                    {t('salesOrder.jobQty', 'Job Qty')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {shipment.lines.map((line, idx) => (
                                  <tr
                                    key={`${shipment.packNum}-${line.orderLine}-${idx}`}
                                    className="border-b border-gray-100 last:border-b-0"
                                  >
                                    <td className="px-3 py-2 text-gray-900 font-medium">
                                      {line.orderLine}
                                    </td>
                                    <td className="px-3 py-2">
                                      {line.partNum ? (
                                        <Link
                                          to={`/parts/${encodeURIComponent(line.partNum)}`}
                                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                        >
                                          {line.partNumUM || line.partNum}
                                        </Link>
                                      ) : (
                                        <span className="text-gray-900">
                                          {line.partNumUM || '—'}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700 max-w-xs truncate">
                                      {line.lineDesc}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-900 whitespace-nowrap">
                                      {formatQtyWithSource(
                                        line.inventoryShipQty,
                                        line.warehouseBin
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-900 whitespace-nowrap">
                                      {formatQtyWithSource(
                                        line.jobShipQty,
                                        line.jobNum
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {shipments.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              {t('salesOrder.noShipments', 'No shipments found')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
